/**
 * Teaching the home-screen install.
 *
 * There is no native app, and for most of what this is used for -- a phone on
 * the counter, a phone in the shop -- an installed PWA is the same thing. The
 * trouble is that nobody discovers it, and the one platform where it needs
 * explaining is the one platform with no API for it.
 *
 * So: ask once, at the moment it is obviously useful, and never again.
 */

/** Already on a home screen. Nothing to teach, and the prompt would be absurd. */
export function isInstalled(): boolean {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true)
    )
  } catch {
    return false
  }
}

/**
 * iOS, where `beforeinstallprompt` does not exist and never will. Adding to
 * the home screen is Share then Add to Home Screen, by hand, so the only help
 * possible is telling someone where to tap.
 */
export function isIOS(): boolean {
  try {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      // iPadOS 13+ reports itself as a Mac, with a touchscreen.
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    )
  } catch {
    return false
  }
}

/*
 * One flag, per device, covering both places that ask. Installing is a
 * per-device act, so a phone that said no should not be told again because a
 * laptop has not been asked -- and one "not now" turns off both the modal and
 * the line on the shopping list. Being asked twice about the same thing is
 * how an app starts feeling like it wants something from you.
 */
const KEY = 'fl_install_nudge'

export function nudgeSilenced(): boolean {
  try {
    return localStorage.getItem(KEY) === 'off'
  } catch {
    // Private mode, blocked storage: ask this once rather than never.
    return false
  }
}

export function silenceNudge(): void {
  try {
    localStorage.setItem(KEY, 'off')
  } catch {
    // Nothing to do. Worst case it is offered again next visit.
  }
}

/** Whether there is anything worth offering on this device, right now. */
export const canNudge = (): boolean => !isInstalled() && !nudgeSilenced()
